<template>
  <div>
    <div class="d-flex align-center">
      <page-title value="VEVOレポート" />
    </div>

    <div class="my-3 d-flex align-center" style="gap: 0.5rem;">
      <div style="width: 160px;">
        <v-select
          hide-details
          density="compact"
          variant="outlined"
          :items="yearOptions"
          v-model="year"
        />
      </div>
      <span class="text-body-2 text-grey">
        各クォーター (3か月) ごとに、園児単位で発達記録を元にしたVEVOレポートを生成します。
      </span>
    </div>

    <v-progress-linear
      v-if="isFetching"
      indeterminate
      color="primary"
      class="my-3"
    />

    <v-row v-if="top" class="my-2">
      <v-col
        v-for="q in top.quarters"
        :key="q.quarter"
        cols="12"
        md="6"
        lg="3"
      >
        <v-card variant="outlined" class="pa-4 h-100" :class="cardClass(q)">
          <div class="d-flex align-center justify-space-between">
            <div>
              <div class="text-overline text-grey">第{{ q.quarter }}クォーター</div>
              <div class="text-h6 font-weight-bold">{{ q.monthsLabel }}</div>
            </div>
            <v-chip
              v-if="q.latestBatch && q.latestBatch.status === 'processing'"
              color="primary"
              size="small"
              label
            >
              <v-icon size="small" start>mdi-progress-clock</v-icon>
              生成中
            </v-chip>
          </div>

          <v-divider class="my-3" />

          <div class="d-flex flex-column" style="gap: 0.4rem;">
            <div class="d-flex align-center justify-space-between">
              <span class="text-body-2">公開済</span>
              <span class="text-body-1 font-weight-bold text-primary">
                {{ q.publishedCount }} / {{ q.totalChildren }} 名
              </span>
            </div>
            <div class="d-flex align-center justify-space-between">
              <span class="text-body-2">下書き</span>
              <span class="text-body-1 font-weight-bold text-amber-darken-3">
                {{ q.draftCount }} 名
              </span>
            </div>
            <div class="d-flex align-center justify-space-between">
              <span class="text-body-2">未作成</span>
              <span class="text-body-1 font-weight-bold text-grey-darken-1">
                {{ q.notGeneratedCount }} 名
              </span>
            </div>
            <v-progress-linear
              :model-value="publishProgress(q)"
              color="primary"
              bg-color="grey-lighten-3"
              height="6"
              rounded
              class="mt-2"
            />
          </div>

          <v-divider class="my-3" />

          <div class="d-flex" style="gap: 0.4rem;">
            <v-btn
              color="primary"
              variant="flat"
              block
              :to="quarterPath(q.quarter)"
            >
              詳細を開く
            </v-btn>
          </div>

          <div
            v-if="q.latestBatch && q.latestBatch.finishedAt"
            class="mt-2 text-caption text-grey"
          >
            最終実行: {{ formatDateTime(q.latestBatch.finishedAt) }}
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-alert
      v-if="!isFetching && top && top.quarters.every(isQuarterEmpty)"
      type="info"
      variant="outlined"
      class="mt-4"
    >
      この年度はまだレポートが生成されていません。詳細を開いて一括生成を実行してください。
    </v-alert>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import dayjs from "dayjs";
import PageTitle from "@/components/atoms/PageTitle.vue";
import CommonUtil from "@/utils/CommonUtil";
import { aiReportTopModule } from "@/store/viewModules/ai-report/aiReportTopModule";
import type {
  AiReportQuarter,
  AiReportQuarterSummary,
} from "@/api/ApiAiReport";

interface DataType {
  year: number;
}

export default defineComponent({
  components: { PageTitle },
  data: (): DataType => ({
    year: parseInt(CommonUtil.getFiscalYear(dayjs().format("YYYY-MM")), 10),
  }),
  computed: {
    top() {
      return aiReportTopModule.top;
    },
    isFetching(): boolean {
      return aiReportTopModule.isFetching;
    },
    yearOptions(): { title: string; value: number }[] {
      const current = parseInt(
        CommonUtil.getFiscalYear(dayjs().format("YYYY-MM")),
        10
      );
      const options: { title: string; value: number }[] = [];
      for (let y = current; y >= current - 5; y--) {
        options.push({ title: `${y}年度`, value: y });
      }
      return options;
    },
  },
  watch: {
    year() {
      this.loadTop();
    },
  },
  mounted() {
    this.loadTop();
  },
  methods: {
    async loadTop() {
      await aiReportTopModule.loadTop({ businessYear: this.year });
    },
    quarterPath(quarter: AiReportQuarter): string {
      return `/ai-report/quarter/${this.year}/${quarter}`;
    },
    publishProgress(q: AiReportQuarterSummary): number {
      if (q.totalChildren === 0) return 0;
      return Math.floor((q.publishedCount / q.totalChildren) * 100);
    },
    isQuarterEmpty(q: AiReportQuarterSummary): boolean {
      return q.publishedCount === 0 && q.draftCount === 0;
    },
    cardClass(q: AiReportQuarterSummary): string {
      if (q.publishedCount === q.totalChildren && q.totalChildren > 0) {
        return "ai-report-top__card ai-report-top__card--done";
      }
      if (q.latestBatch && q.latestBatch.status === "processing") {
        return "ai-report-top__card ai-report-top__card--processing";
      }
      return "ai-report-top__card";
    },
    formatDateTime(value: string | null): string {
      if (!value) return "—";
      return dayjs(value).format("YYYY/MM/DD HH:mm");
    },
  },
});
</script>

<style lang="scss" scoped>
.ai-report-top__card {
  height: 100%;
  transition: border-color 0.2s ease;

  &--done {
    border-color: #029ec0 !important;
    background-color: #f5fbfc;
  }

  &--processing {
    border-color: #ffb74d !important;
    background-color: #fff8e1;
  }
}
</style>
